import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlContainer, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonComponent, InputComponent, TypographyComponent } from '@purolator/ui-kit-angular';
import { TranslationService, TranslatePipe } from '../../../../core/i18n';
import type { AddressAutocompleteSelection } from '../../../../core/models/address-autocomplete';
import { buildCanadianProvinceOptions } from '../../constants/canadian-provinces';
import {
  blurAddressField,
  collectAddressSectionErrors,
  getAddressFieldErrorMessage,
  RETURN_ADDRESS_FIELD_ORDER,
  type AddressValidatedFieldKey,
} from '../../utils/address-form-errors';
import { FloatingSelect } from '../../../../shared/components/floating-select/floating-select';
import {
  FormErrorSummary,
  type FormErrorSummaryItem,
} from '../../../../shared/components/form-error-summary/form-error-summary';
import { AddressSuggestedAddressPanel } from '../address-suggested-address-panel/address-suggested-address-panel';
import { OptionalAddressLines } from '../optional-address-lines/optional-address-lines';
import { PhoneFieldGroup } from '../phone-field-group/phone-field-group';
import { StreetAddressAutocompleteField } from '../../../../shared/components/street-address-autocomplete-field/street-address-autocomplete-field';
import { DisableBrowserAutofillDirective } from '../../../../shared/directives/disable-browser-autofill.directive';
import type { AddressSuggestionPresentation } from '../../utils/map-validate-results-to-suggestions';
import { detectPostalKeywordInAddress } from '../../utils/detect-postal-keyword-address';

/**
 * Optional return address fields shown when the Ship To return toggle is checked.
 */
@Component({
  selector: 'app-return-address-section',
  imports: [
    ReactiveFormsModule,
    TypographyComponent,
    InputComponent,
    ButtonComponent,
    TranslatePipe,
    FormErrorSummary,
    FloatingSelect,
    AddressSuggestedAddressPanel,
    OptionalAddressLines,
    PhoneFieldGroup,
    StreetAddressAutocompleteField,
    DisableBrowserAutofillDirective,
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
  templateUrl: './return-address-section.html',
  styleUrl: './return-address-section.scss',
})
export class ReturnAddressSection implements OnInit {
  private readonly translation = inject(TranslationService);
  private readonly controlContainer = inject(ControlContainer);
  private readonly destroyRef = inject(DestroyRef);

  /** Bumped on field blur so inline errors refresh after leave without submit. */
  private readonly fieldInteractionVersion = signal(0);

  /** Incremented on street address or address line 3 changes to drive rural route detection. */
  private readonly ruralRouteVersion = signal(0);

  readonly submitted = input(false);
  readonly validationAttempt = input(0);
  readonly addressLinesExpanded = input(false);
  readonly addressSuggestion = input<AddressSuggestionPresentation | null>(null);
  readonly clearRequested = output<void>();
  readonly expandLinesRequested = output<void>();
  /** Emits the chosen autocomplete item for field population via retrieve. */
  readonly streetAddressSuggestionSelected = output<AddressAutocompleteSelection>();
  readonly useSuggestedAddress = output<void>();
  readonly keepSuggestedAddressEntry = output<void>();

  /** Return-address nested form group bound via formGroupName from the parent form. */
  protected get addressFormGroup(): FormGroup {
    const rootForm = this.controlContainer.control as FormGroup;
    return rootForm.get('returnAddress') as FormGroup;
  }

  protected readonly suggestionPanelId = 'ship-from-ship-to-suggested-returnAddress';

  protected readonly streetDescribedByIds = computed((): readonly string[] =>
    this.addressSuggestion() ? [`${this.suggestionPanelId}-description`] : [],
  );

  protected readonly provinceOptions = computed(() => {
    this.translation.revision();
    return buildCanadianProvinceOptions((key) => this.translation.translate(key));
  });

  /**
   * Phone Number is optional on the return address unless it is a PO Box, Rural Route,
   * or General Delivery address (OM-693).
   */
  protected readonly phoneRequired = computed(() => {
    this.ruralRouteVersion();
    const group = this.addressFormGroup;
    return detectPostalKeywordInAddress(
      String(group.get('streetAddress')?.value ?? ''),
      String(group.get('addressLine3')?.value ?? ''),
    );
  });

  protected readonly sectionErrorMessages = computed((): FormErrorSummaryItem[] => {
    this.translation.revision();
    this.validationAttempt();

    const summaryExtras =
      this.addressSuggestion()?.mode === 'suggestion'
        ? [
            {
              emphasis: this.translation.translate('createShipment.fields.streetAddress'),
              detail: this.translation.translate(
                'createShipment.validation.streetAddressUnverifiedSummaryDetail',
              ),
            },
          ]
        : undefined;

    return (
      collectAddressSectionErrors(
        this.addressFormGroup,
        (key, params) => this.translation.translate(key, params),
        this.submitted(),
        RETURN_ADDRESS_FIELD_ORDER,
        summaryExtras,
        'returnAddress',
      )?.summaryMessages ?? []
    );
  });

  /**
   * Subscribes to streetAddress and addressLine3 changes so the phone required marker
   * updates as soon as a rural route or PO Box keyword is typed.
   */
  ngOnInit(): void {
    const group = this.addressFormGroup;
    const bump = (): void => this.ruralRouteVersion.update((version) => version + 1);

    for (const field of ['streetAddress', 'addressLine3'] as const) {
      group.get(field)?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(bump);
    }
  }

  /**
   * Builds a section-scoped input name so browsers do not autofill AVS fields heuristically.
   */
  protected avsFieldName(field: string): string {
    return `returnAddress-${field}`;
  }

  /**
   * Returns the inline validation message for a return-address field after blur or submit.
   * Prioritises suggestion unverified and not-found errors over general field errors.
   */
  protected fieldError(field: AddressValidatedFieldKey): string | null {
    this.translation.revision();
    this.fieldInteractionVersion();

    if (field === 'streetAddress' && this.addressSuggestion()?.mode === 'suggestion') {
      return this.translation.translate('createShipment.validation.streetAddressUnverified');
    }

    if (
      this.submitted() &&
      field === 'streetAddress' &&
      this.addressFormGroup.hasError('addressNotFound')
    ) {
      return this.translation.translate('createShipment.validation.addressNotFoundField');
    }

    return getAddressFieldErrorMessage(
      this.addressFormGroup.get(field),
      field,
      (key, params) => this.translation.translate(key, params),
      this.submitted(),
    );
  }

  /**
   * Marks a field touched on blur so applicable UI validators can show inline messages.
   */
  protected onFieldBlur(field: AddressValidatedFieldKey): void {
    blurAddressField(this.addressFormGroup.get(field));
    this.fieldInteractionVersion.update((version) => version + 1);
  }

  /**
   * Emits when the user clears the return address section.
   */
  protected onClearForm(): void {
    this.clearRequested.emit();
  }

  /**
   * Expands the optional Address Line 2 and Address Line 3 fields for the return section.
   */
  protected onExpandAddressLines(): void {
    this.expandLinesRequested.emit();
  }

  /**
   * Emits when the user accepts the AVS suggested address for the return section.
   */
  protected onUseSuggestedAddress(): void {
    this.useSuggestedAddress.emit();
  }

  /**
   * Emits when the user keeps their original return address entry.
   */
  protected onKeepSuggestedAddressEntry(): void {
    this.keepSuggestedAddressEntry.emit();
  }
}
